import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { getIntegrationIcon } from '@/integrations/integration-icons';

import { parseNotesDoctorReport } from './apple-notes-setup';
import { Callout } from './callout';
import {
  CapabilityRow,
  CheckRow,
  ListSection,
  type Check,
  type CheckState,
} from './connect-checklist';
import { DialogHeader } from './dialog-header';
import { ModalCard } from './modal-card';
import { openFullDiskAccessSettings } from './server-permissions';
import {
  oneClickProblemCopy,
  type OneClickConnectForm,
} from './use-one-click-connect';

import type { OneClickServer } from './one-click-server';

const CAPABILITIES = [
  { title: 'Search and read notes', note: 'All accounts' },
  { title: 'Create, edit and move notes and folders' },
  { title: 'Delete notes', note: 'Go to Recently Deleted' },
  { title: 'Read checklists and attachments' },
];

const FULL_DISK_ACCESS_USE = 'Used for checklist state and note details.';

function checksFor(form: OneClickConnectForm): Check[] {
  const report = parseNotesDoctorReport(form.testResult);
  const checking = form.phase === 'testing';
  const fullDiskAccessMissing =
    form.missingPermissions.includes('fullDiskAccess');

  const notesState: CheckState = checking
    ? 'checking'
    : !report?.controlAllowed
      ? 'pending'
      : report.accountNames
        ? 'ok'
        : 'blocked';
  const controlState: CheckState = checking
    ? 'checking'
    : !report
      ? 'pending'
      : report.controlAllowed
        ? 'ok'
        : 'blocked';
  const fullDiskAccessState: CheckState = checking
    ? 'checking'
    : fullDiskAccessMissing
      ? 'waiting'
      : form.testResult
        ? 'ok'
        : 'pending';

  return [
    {
      title: 'Notes is set up',
      state: notesState,
      detail:
        notesState === 'ok'
          ? `Accounts: ${report?.accountNames}`
          : notesState === 'blocked'
            ? 'Open Notes and add an account, then try again.'
            : 'Checked when you connect.',
    },
    {
      title: 'Control Notes',
      state: controlState,
      detail:
        controlState === 'ok'
          ? 'Allowed. macOS asked when you tested.'
          : controlState === 'blocked'
            ? 'Not allowed. Turn on Notes for Littlebird in Privacy & Security, then Automation.'
            : 'macOS asks to let Littlebird control Notes when you connect.',
    },
    {
      title: 'Full Disk Access',
      state: fullDiskAccessState,
      detail:
        fullDiskAccessState === 'ok'
          ? `On. ${FULL_DISK_ACCESS_USE}`
          : fullDiskAccessState === 'waiting'
            ? 'Turn on Littlebird in Privacy & Security. We connect as soon as it’s on.'
            : FULL_DISK_ACCESS_USE,
      action: fullDiskAccessState === 'waiting' && (
        <Button
          variant="outline"
          size="sm"
          className="shrink-0"
          onClick={openFullDiskAccessSettings}
        >
          Open System Settings
        </Button>
      ),
    },
  ];
}

export const AppleNotesConnectModal = ({
  server,
  form,
  onCancel,
}: {
  server: OneClickServer;
  form: OneClickConnectForm;
  onCancel: () => void;
}): React.ReactElement => {
  const IntegrationIcon = getIntegrationIcon(server.id);
  const checks = checksFor(form);
  // A missing permission, and a `doctor` report that failed, each show up in
  // their check's row
  const checksExplainProblem =
    form.problem === 'permission_denied' ||
    (form.problem === 'connection_failed' &&
      parseNotesDoctorReport(form.testResult) !== null);

  return (
    <ModalCard onClose={onCancel} closeDisabled={!form.canCancel}>
      <DialogHeader
        icon={<IntegrationIcon className="size-5" />}
        title={`Connect ${server.name}`}
        subtitle={<span className="block">{server.description}</span>}
      />
      <YStack className="gap-5 p-6">
        <ListSection label="Littlebird will be able to">
          {CAPABILITIES.map((capability) => (
            <CapabilityRow key={capability.title} {...capability} />
          ))}
        </ListSection>

        <ListSection label="Checks on this Mac">
          {checks.map((check) => (
            <CheckRow key={check.title} check={check} />
          ))}
        </ListSection>

        {form.problem && !checksExplainProblem && (
          <Callout tone="danger" icon={<Icons.warn className="size-4" />}>
            {oneClickProblemCopy(form.problem, server.name)}
          </Callout>
        )}
      </YStack>
      <XStack className="items-center justify-end gap-2 border-t border-border p-4 px-6">
        <Button
          variant="ghost"
          size="sm"
          disabled={!form.canCancel}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          variant="default"
          size="sm"
          disabled={!form.canConnect}
          isLoading={form.busy}
          loadingText={form.phase === 'saving' ? 'Finishing…' : 'Connecting…'}
          onClick={() => void form.connect()}
        >
          {form.problem ? 'Try again' : 'Connect'}
        </Button>
      </XStack>
    </ModalCard>
  );
};
